import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play, Pause, RotateCcw, X, Minus, Plus,
  ChevronUp, ChevronDown, Settings, Maximize2, Minimize2,
  Move, AlignLeft, Zap, Loader2
} from 'lucide-react';
import requests from '../../../services/httpServices';

const FONT_SIZE_MAP = {
  xs: 16,
  s: 20,
  m: 28,
  l: 36,
  xl: 48
};

const LINE_HEIGHT_MAP = {
  tight: 1.3,
  normal: 1.6,
  relaxed: 2
};

// Custom Slider Component
const Slider = ({ value, onChange, min = 0, max = 100, step = 1 }) => (
  <input
    type="range"
    value={value}
    onChange={(e) => onChange(parseFloat(e.target.value))}
    min={min}
    max={max}
    step={step}
    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-teal-500"
  />
);

/**
 * PrompterViewer - Host-only teleprompter overlay
 *
 * CRITICAL: This component renders as a PORTAL to ensure it:
 * - Lives OUTSIDE the stage/layout DOM tree
 * - Never affects video grid, participant tiles, or recordings
 * - Is only visible to the host
 * - Has its own z-index layer above stage but below modals
 */
export default function PrompterViewer({
  prompter,
  isOpen,
  onClose,
  sttLiveText, // Live Agora STT text (interim + final) from MeetingContainerNew
  sttBotActive, // Whether STT bot is currently running
  onEnsureStt, // Function to start STT bot if not running
  windowMode = 'docked', // 'docked' | 'floating' | 'full'
  onWindowModeChange
}) {
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [scrollSpeed, setScrollSpeed] = useState(prompter?.settings?.scrollSpeed ?? 10);
  const [fontSize, setFontSize] = useState(prompter?.settings?.fontSize || 'm');
  const [lineHeight, setLineHeight] = useState(prompter?.settings?.lineHeight || 'normal');
  const [mirroring, setMirroring] = useState(prompter?.settings?.mirroring || false);
  const [showSettings, setShowSettings] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [highlightLine, setHighlightLine] = useState(prompter?.settings?.highlightActiveLine ?? true);
  const [focusMode, setFocusMode] = useState(prompter?.settings?.focusMode ?? false);
  const [focusLines, setFocusLines] = useState(prompter?.settings?.focusLines || 3);
  const [isMinimized, setIsMinimized] = useState(false);
  const [opacity, setOpacity] = useState(0.35); // Very transparent by default - user can see screen behind

  // AI Auto-detect state (uses Agora STT transcriptions, NOT Web Speech API)
  const [aiAutoDetect, setAiAutoDetect] = useState(prompter?.settings?.autoDetectSpeed || false);
  const [aiListening, setAiListening] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [matchedWordIndex, setMatchedWordIndex] = useState(-1);
  const [lastHeard, setLastHeard] = useState("");
  const aiSpeechBufferRef = useRef(""); // accumulated speech from STT
  const aiSentenceIndexRef = useRef(0);
  const aiDetectTimerRef = useRef(null);
  const matchedWordIndexRef = useRef(-1);
  const lastMatchTimeRef = useRef(Date.now());
  const aiScrollAnimRef = useRef(null);
  const aiAutoDetectRef = useRef(false);

  // Floating window position - now works for all modes
  const [position, setPosition] = useState({ x: window.innerWidth - 450, y: 64 });
  const [size, setSize] = useState({ width: 420, height: 500 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0 });
  const containerRef = useRef(null);

  const contentRef = useRef(null);
  const scrollRef = useRef(null);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(0);
  const scrollPositionRef = useRef(0);
  const isScrollingRef = useRef(false);
  const scrollSpeedRef = useRef(scrollSpeed);

  const content = prompter?.content || prompter?.script || '';
  const lines = content.split('\n');

  // Update settings when prompter changes
  useEffect(() => {
    if (prompter?.settings) {
      const settings = prompter.settings;
      if (settings.scrollSpeed !== undefined) setScrollSpeed(settings.scrollSpeed);
      if (settings.fontSize) setFontSize(settings.fontSize);
      if (settings.lineHeight) setLineHeight(settings.lineHeight);
      if (settings.mirroring !== undefined) setMirroring(settings.mirroring);
      if (settings.highlightActiveLine !== undefined) setHighlightLine(settings.highlightActiveLine);
      if (settings.focusMode !== undefined) setFocusMode(settings.focusMode);
      if (settings.focusLines !== undefined) setFocusLines(settings.focusLines);
    }
  }, [prompter]);

  // Keep refs in sync with state
  useEffect(() => {
    scrollPositionRef.current = scrollPosition;
  }, [scrollPosition]);

  useEffect(() => {
    isScrollingRef.current = isScrolling;
  }, [isScrolling]);

  useEffect(() => {
    aiAutoDetectRef.current = aiAutoDetect;
  }, [aiAutoDetect]);

  useEffect(() => {
    scrollSpeedRef.current = scrollSpeed;
  }, [scrollSpeed]);

  // Auto-start STT bot when AI mode is enabled (so teleprompter gets speech data)
  useEffect(() => {
    console.log('[PrompterViewer] STT check:', { aiAutoDetect, isScrolling, sttBotActive, hasEnsureStt: !!onEnsureStt });
    if (aiAutoDetect && isScrolling && !sttBotActive && onEnsureStt) {
      console.log('[PrompterViewer] STT bot not active, starting...');
      onEnsureStt();
    }
  }, [aiAutoDetect, isScrolling, sttBotActive, onEnsureStt]);

  // Parse script into words for AI matching
  const scriptWordsRef = useRef([]);
  const sentencesRef = useRef([]);
  useEffect(() => {
    const words = content.split(/(\s+)/).filter(Boolean);
    const scriptWords = [];
    words.forEach((token) => {
      if (/^\s+$/.test(token)) {
        scriptWords.push({ raw: token, normalized: '', isSpace: true });
      } else {
        const norm = token.toLowerCase().replace(/[^a-z0-9'']/g, '');
        const idx = scriptWords.length;
        scriptWords.push({ raw: token, normalized: norm, isSpace: false, index: idx });
      }
    });
    scriptWordsRef.current = scriptWords;
    sentencesRef.current = content
      .replace(/\n+/g, ' ')
      .split(/(?<=[.!?])\s+/)
      .map(s => s.trim())
      .filter(Boolean);
  }, [content]);

  // Smooth scroll target — AI sets this, animation eases toward it
  const aiTargetScrollRef = useRef(0);
  // Track where we are in the script (index into actualWords array)
  const scriptCursorRef = useRef(0);

  // Fuzzy word comparison — handles STT inaccuracy, partial words, synonyms
  const wordsMatch = useCallback((spoken, script) => {
    if (!spoken || !script) return false;
    if (spoken === script) return true;
    // Prefix match (STT often truncates or extends words)
    if (spoken.length >= 3 && script.length >= 3) {
      if (spoken.startsWith(script) || script.startsWith(spoken)) return true;
    }
    // Levenshtein-like: allow 1 char difference for words >= 4 chars
    if (spoken.length >= 4 && script.length >= 4 && Math.abs(spoken.length - script.length) <= 1) {
      let diffs = 0;
      const minLen = Math.min(spoken.length, script.length);
      for (let i = 0; i < minLen; i++) {
        if (spoken[i] !== script[i]) diffs++;
        if (diffs > 1) break;
      }
      if (diffs <= 1) return true;
    }
    return false;
  }, []);

  // Advance the script cursor based on what was just spoken.
  // Only looks at the NEXT ~20 words in the script (2 sentences worth).
  // Walks forward one word at a time — never jumps far ahead.
  const doClientSideMatch = useCallback((spokenText) => {
    if (!spokenText) return;
    const actualWords = scriptWordsRef.current.filter(w => !w.isSpace);
    if (actualWords.length === 0) return;

    // Normalize the last few spoken words
    const spokenWords = spokenText.toLowerCase().split(/\s+/).filter(Boolean);
    const recent = spokenWords.slice(-5).map(w => w.replace(/[^a-z0-9'']/g, '')).filter(w => w.length >= 2);
    if (recent.length === 0) return;

    const cursor = scriptCursorRef.current;
    // Only search a small window ahead: ~20 words (roughly 2 sentences)
    const windowEnd = Math.min(actualWords.length, cursor + 20);

    // Strategy: find the farthest FORWARD position within the window where
    // we can match at least 1 of the recent spoken words.
    // Walk word by word from cursor — find the best sequential match.
    let bestPos = cursor;

    // Try to match each spoken word against the script window sequentially
    let scriptPos = cursor;
    for (let si = 0; si < recent.length; si++) {
      const spokenNorm = recent[si];
      // Look forward from scriptPos for this spoken word (max a few words ahead to allow skips)
      let found = false;
      for (let wi = scriptPos; wi < Math.min(scriptPos + 6, windowEnd); wi++) {
        if (wordsMatch(spokenNorm, actualWords[wi]?.normalized)) {
          scriptPos = wi + 1; // advance past this match
          bestPos = Math.max(bestPos, wi);
          found = true;
          break;
        }
      }
      // If not found, skip this spoken word (speaker may have ad-libbed)
      if (!found) continue;
    }

    // Only advance if we matched something ahead of current cursor
    if (bestPos > cursor) {
      scriptCursorRef.current = bestPos;
      const newIdx = actualWords[bestPos].index;

      // Only update React state if position meaningfully changed
      if (newIdx > matchedWordIndexRef.current) {
        matchedWordIndexRef.current = newIdx;
        setMatchedWordIndex(newIdx);
        lastMatchTimeRef.current = Date.now();

        // Set scroll target so the CURRENT reading position is at the CENTER highlight bar,
        // with upcoming text visible below. We look ~12 words ahead and position
        // that look-ahead point at ~15% from top of viewport — this puts the
        // speaker's current words right at the center highlight bar.
        if (scrollRef.current) {
          const lookAheadIdx = Math.min(bestPos + 12, actualWords.length - 1);
          const lookAheadWordEl = scrollRef.current.querySelector(`[data-word-idx="${actualWords[lookAheadIdx].index}"]`);
          const matchedWordEl = scrollRef.current.querySelector(`[data-word-idx="${newIdx}"]`);
          const targetEl = lookAheadWordEl || matchedWordEl;
          if (targetEl) {
            const viewportHeight = contentRef.current?.clientHeight || 400;
            // Position so current reading position lands at center of viewport
            // targetEl is ~12 words AHEAD, so offset it to top 15% —
            // this pushes the actual matched word down to roughly center
            aiTargetScrollRef.current = Math.max(0, targetEl.offsetTop - viewportHeight * 0.15);
          } else {
            const totalWords = actualWords.length;
            const scrollHeight = scrollRef.current.scrollHeight;
            const viewportHeight = contentRef.current?.clientHeight || 400;
            const lookAheadRatio = (lookAheadIdx / totalWords);
            aiTargetScrollRef.current = Math.max(0, lookAheadRatio * scrollHeight - viewportHeight * 0.15);
          }
        }
      }
    }
  }, [wordsMatch]);

  // AI Auto-detect: Feed Agora STT transcriptions into word matcher
  // Only passes the LATEST STT segment — matching walks forward from cursor
  const lastSttTsRef = useRef(0);
  useEffect(() => {
    if (!sttLiveText || !aiAutoDetect || !isScrolling) return;
    if (sttLiveText.ts === lastSttTsRef.current) return;
    lastSttTsRef.current = sttLiveText.ts;

    const text = sttLiveText.text;
    if (!text) return;

    // Accumulate for GPT refinement, but matching uses only latest text
    if (sttLiveText.isFinal) {
      aiSpeechBufferRef.current += ' ' + text;
      if (aiSpeechBufferRef.current.length > 600) {
        aiSpeechBufferRef.current = aiSpeechBufferRef.current.slice(-400);
      }
    }

    setLastHeard(text.split(/\s+/).slice(-6).join(' '));
    setAiListening(true);
    console.log('[PrompterViewer] STT received:', text.slice(0, 60), '| cursor:', scriptCursorRef.current);

    // Match ONLY the latest spoken segment against script (no accumulated history)
    doClientSideMatch(text);
  }, [sttLiveText, aiAutoDetect, isScrolling, doClientSideMatch]);

  // Periodically send accumulated speech to GPT for position refinement
  // Only sends a small window of the script (current sentence + 2 ahead) to prevent far jumps
  useEffect(() => {
    if (!aiAutoDetect || !isScrolling) {
      if (aiDetectTimerRef.current) {
        clearInterval(aiDetectTimerRef.current);
        aiDetectTimerRef.current = null;
      }
      setAiListening(false);
      aiSpeechBufferRef.current = "";
      return;
    }

    aiDetectTimerRef.current = setInterval(async () => {
      const recentSpeech = aiSpeechBufferRef.current.trim();
      if (!recentSpeech || recentSpeech.length < 15) return;

      // Build a constrained script window: only sentences near current position
      const sentences = sentencesRef.current;
      const curSentIdx = aiSentenceIndexRef.current;
      const windowStart = Math.max(0, curSentIdx - 1);
      const windowEnd = Math.min(sentences.length, curSentIdx + 3);
      const scriptWindow = sentences.slice(windowStart, windowEnd).join(' ');

      if (!scriptWindow) return;

      setAiProcessing(true);
      try {
        const resp = await requests.post('/openai/prompter-detect-position', {
          script: scriptWindow,
          currentSentenceIndex: Math.min(1, curSentIdx), // relative to window
          recentSpeech: recentSpeech.slice(-200),
          lookAheadSentences: 2,
        });

        const { sentenceIndex, confidence } = resp;
        // Map back to absolute sentence index
        const absoluteSentIdx = windowStart + sentenceIndex;
        if (confidence >= 0.5 && absoluteSentIdx > aiSentenceIndexRef.current) {
          aiSentenceIndexRef.current = absoluteSentIdx;
          // Don't directly update scroll — let the next STT match handle positioning
          // This just advances the sentence tracker for the next GPT window
        }
      } catch (err) {
        // Silent fail — GPT refinement is optional
      } finally {
        setAiProcessing(false);
      }
    }, 5000);

    return () => {
      if (aiDetectTimerRef.current) {
        clearInterval(aiDetectTimerRef.current);
        aiDetectTimerRef.current = null;
      }
    };
  }, [aiAutoDetect, isScrolling]);

  // Calculate pixels per second from speed slider (0-100 -> 10-300 px/s)
  const getPixelsPerSecond = () => {
    return 10 + (scrollSpeedRef.current / 100) * 290;
  };

  // Track smoothed scroll velocity for natural acceleration/deceleration
  const scrollVelocityRef = useRef(30); // start at base reading speed

  // Unified smooth scrolling — handles both manual speed mode and AI mode
  // In AI mode: ALWAYS moves forward at a smooth pace.
  //   - Base speed ~30px/s (natural reading pace, like speed 5-6)
  //   - Gently accelerates when target is ahead (speaker is faster)
  //   - Gently decelerates toward base speed when caught up
  //   - Decelerates to near-zero only when speaker stops (no new matches for a while)
  // In manual mode: constant speed scroll as before
  const animateScroll = useCallback((timestamp) => {
    if (!isScrollingRef.current || !scrollRef.current) return;

    if (!lastTimeRef.current) lastTimeRef.current = timestamp;
    const deltaTime = Math.min((timestamp - lastTimeRef.current) / 1000, 0.1);
    lastTimeRef.current = timestamp;

    const maxScroll = scrollRef.current.scrollHeight - (contentRef.current?.clientHeight || 300);
    let newPosition = scrollPositionRef.current;

    if (aiAutoDetectRef.current) {
      const target = aiTargetScrollRef.current;
      const current = scrollPositionRef.current;
      const distance = target - current;
      const timeSinceMatch = (Date.now() - lastMatchTimeRef.current) / 1000;

      // Desired speed based on context — responsive to speech pace
      let desiredSpeed;

      if (timeSinceMatch > 6) {
        // No speech detected for 6+ seconds — speaker stopped, ease to halt
        desiredSpeed = 0;
      } else if (distance > 400) {
        // Target is very far ahead — fast catch-up
        desiredSpeed = Math.min(400, 60 + distance * 1.2);
      } else if (distance > 150) {
        // Target is far ahead — speaker is reading fast
        desiredSpeed = 60 + distance * 0.9;
      } else if (distance > 40) {
        // Target is moderately ahead — accelerate to keep up
        desiredSpeed = 50 + distance * 0.7;
      } else if (distance > 0) {
        // Nearly caught up — comfortable reading speed
        desiredSpeed = 40;
      } else {
        // We're at or past target — gentle forward drift (don't stop abruptly)
        // The next match will push the target forward again
        desiredSpeed = timeSinceMatch > 3 ? 8 : 25;
      }

      // Smooth the velocity change — accelerate quickly, decelerate gently
      const currentVel = scrollVelocityRef.current;
      const easeRate = desiredSpeed > currentVel ? 3.5 : 2.0; // accelerate fast, decelerate gentle
      scrollVelocityRef.current = currentVel + (desiredSpeed - currentVel) * Math.min(deltaTime * easeRate, 1);

      // Always move forward (never backward)
      newPosition = current + Math.max(0, scrollVelocityRef.current) * deltaTime;
    } else {
      // Manual speed mode
      const pxPerSecond = getPixelsPerSecond();
      newPosition = scrollPositionRef.current + (pxPerSecond * deltaTime);
    }

    if (newPosition >= maxScroll) {
      scrollPositionRef.current = maxScroll;
      setScrollPosition(maxScroll);
      setIsScrolling(false);
      return;
    }

    scrollPositionRef.current = newPosition;
    setScrollPosition(newPosition);
    animationRef.current = requestAnimationFrame(animateScroll);
  }, []);

  // Start/stop animation — runs in BOTH manual and AI modes
  useEffect(() => {
    if (isScrolling && countdown === 0) {
      lastTimeRef.current = 0;
      scrollVelocityRef.current = 30; // start at base reading speed
      aiTargetScrollRef.current = scrollPositionRef.current;
      animationRef.current = requestAnimationFrame(animateScroll);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isScrolling, countdown, animateScroll]);

  // Apply scroll position with GPU acceleration
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.style.transform = `translateY(-${scrollPosition}px) ${mirroring ? 'scaleX(-1)' : ''}`;
    }
  }, [scrollPosition, mirroring]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Handle start with optional countdown - auto-enables AI listening
  const handleStart = () => {
    const countdownSeconds = prompter?.settings?.countdownSeconds || 0;
    if (countdownSeconds > 0 && !isScrolling) {
      setCountdown(countdownSeconds);
    }
    // Auto-enable AI listening when Play is pressed
    setAiAutoDetect(true);
    setIsScrolling(true);
  };

  const handlePause = () => {
    setIsScrolling(false);
  };

  const handleReset = () => {
    setIsScrolling(false);
    setScrollPosition(0);
    setCountdown(0);
    // Reset AI state
    matchedWordIndexRef.current = -1;
    setMatchedWordIndex(-1);
    scriptCursorRef.current = 0;
    aiSentenceIndexRef.current = 0;
    aiSpeechBufferRef.current = "";
    aiTargetScrollRef.current = 0;
    lastMatchTimeRef.current = Date.now();
  };

  const handleSpeedChange = (delta) => {
    setScrollSpeed(prev => Math.max(0, Math.min(100, prev + delta)));
  };

  // Dragging - only works for 'floating' mode
  const handleDragStart = (e) => {
    // Only allow dragging in floating mode
    if (windowMode !== 'floating') return;
    // Don't start drag if clicking on buttons or controls
    if (e.target.closest('button') || e.target.closest('.control-area') || e.target.closest('input')) return;

    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: position.x,
      startPosY: position.y
    };
  };

  const handleDrag = useCallback((e) => {
    if (!isDragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;

    // Constrain to viewport
    const newX = Math.max(0, Math.min(window.innerWidth - size.width, dragRef.current.startPosX + dx));
    const newY = Math.max(0, Math.min(window.innerHeight - 100, dragRef.current.startPosY + dy));

    setPosition({ x: newX, y: newY });
  }, [isDragging, size.width]);

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      document.body.style.userSelect = 'none';
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleDrag]);

  // Calculate current line for highlighting
  const getCurrentLineIndex = () => {
    if (!scrollRef.current) return 0;
    const lineHeightPx = FONT_SIZE_MAP[fontSize] * LINE_HEIGHT_MAP[lineHeight];
    return Math.floor(scrollPosition / lineHeightPx);
  };

  if (!isOpen || !prompter) return null;

  // Z-index values:
  // - Stage content: z-10 to z-20
  // - Teleprompter overlay: z-[2000] (above stage, below modals)
  // - System modals: z-[9000]+
  // Dragging only enabled for 'floating' mode
  const getContainerStyles = () => {
    const baseZ = 'z-[2000]'; // Dedicated teleprompter layer

    if (windowMode === 'full') {
      return `fixed inset-0 ${baseZ}`;
    }

    // Only add drag cursor and visual feedback for floating mode
    if (windowMode === 'floating') {
      const dragCursor = isDragging ? 'cursor-grabbing' : 'cursor-grab';
      const hoverRing = isDragging ? 'ring-2 ring-purple-500/50' : 'hover:ring-1 hover:ring-white/20';
      return `fixed ${baseZ} ${dragCursor} ${hoverRing} rounded-xl overflow-hidden shadow-2xl transition-shadow duration-200`;
    }

    // Docked mode - fixed position, no drag
    return `fixed ${baseZ} rounded-xl overflow-hidden shadow-2xl`;
  };

  const getFloatingStyle = () => {
    if (windowMode === 'full') return {};

    // Floating mode - user-controlled position
    if (windowMode === 'floating') {
      return {
        left: position.x,
        top: position.y,
        width: size.width,
        height: isMinimized ? 'auto' : size.height
      };
    }

    // Docked mode - fixed position on right side
    return {
      right: 16,
      top: 64,
      width: 420,
      height: isMinimized ? 'auto' : 500
    };
  };

  const currentLine = getCurrentLineIndex();

  // Render as portal to ensure complete separation from stage DOM
  // This guarantees teleprompter never interferes with layout/recording
  const teleprompterContent = (
    <AnimatePresence>
      <motion.div
        ref={containerRef}
        id="teleprompter-overlay"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={getContainerStyles()}
        style={{
          ...getFloatingStyle(),
          pointerEvents: 'auto',
          boxShadow: isDragging ? '0 12px 48px rgba(106, 31, 191, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: `1px solid rgba(255, 255, 255, ${isDragging ? '0.3' : '0.1'})`,
        }}
        onMouseDown={handleDragStart}
        data-teleprompter="true"
        data-host-only="true"
      >
        <div
          className="h-full flex flex-col backdrop-blur-xl border-l border-white/10"
          style={{ backgroundColor: `rgba(0, 18, 51, ${opacity})` }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between p-3 border-b border-white/10"
          >
            <div className="flex items-center gap-2">
              {windowMode === 'floating' && (
                <Move className={`w-4 h-4 text-gray-400 ${isDragging ? '' : 'animate-pulse'}`} />
              )}
              <AlignLeft className="w-4 h-4 text-teal-400" />
              <span className="text-sm font-medium text-white truncate max-w-32">
                {prompter.name}
              </span>
              {windowMode === 'floating' && (
                <span className="text-xs text-gray-500 hidden md:inline">(Drag to move)</span>
              )}
            </div>

            <div className="flex items-center gap-1">
              {windowMode === 'floating' && (
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1.5 hover:bg-white/10 rounded transition-colors"
                >
                  {isMinimized ? (
                    <Maximize2 className="w-4 h-4 text-gray-400" />
                  ) : (
                    <Minimize2 className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              )}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-1.5 rounded transition-colors ${
                  showSettings ? 'bg-white/10 text-white' : 'hover:bg-white/10 text-gray-400'
                }`}
              >
                <Settings className="w-4 h-4" />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-white/10 rounded transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Settings Panel */}
          <AnimatePresence>
            {showSettings && !isMinimized && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-b border-white/10 overflow-hidden"
              >
                <div className="p-3 space-y-3 control-area" onMouseDown={(e) => e.stopPropagation()}>
                  {/* Opacity Slider */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-400">Transparency</span>
                      <span className="text-xs text-white">{Math.round(opacity * 100)}%</span>
                    </div>
                    <Slider
                      value={opacity}
                      onChange={setOpacity}
                      min={0.15}
                      max={1}
                      step={0.05}
                    />
                  </div>

                  {/* Speed Slider - hidden when AI auto-detect is on */}
                  {!aiAutoDetect ? (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-400">Speed</span>
                        <span className="text-xs text-white">{scrollSpeed}</span>
                      </div>
                      <Slider
                        value={scrollSpeed}
                        onChange={setScrollSpeed}
                        min={0}
                        max={100}
                        step={1}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                      <Zap className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                      <span className="text-xs text-purple-300">AI is following your speech</span>
                      {aiListening && <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />}
                    </div>
                  )}

                  {/* Font Size */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">Font Size</span>
                    <div className="flex gap-1">
                      {Object.keys(FONT_SIZE_MAP).map(size => (
                        <button
                          key={size}
                          onClick={() => setFontSize(size)}
                          className={`px-2 py-1 text-xs rounded ${
                            fontSize === size
                              ? 'bg-teal-500 text-white'
                              : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          {size.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Options */}
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-xs text-gray-400">
                      <input
                        type="checkbox"
                        checked={mirroring}
                        onChange={(e) => setMirroring(e.target.checked)}
                        className="rounded"
                      />
                      Mirror
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-400">
                      <input
                        type="checkbox"
                        checked={highlightLine}
                        onChange={(e) => setHighlightLine(e.target.checked)}
                        className="rounded"
                      />
                      Highlight Line
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-400">
                      <input
                        type="checkbox"
                        checked={focusMode}
                        onChange={(e) => setFocusMode(e.target.checked)}
                        className="rounded"
                      />
                      Focus Mode
                    </label>
                    {focusMode && (
                      <select
                        value={focusLines}
                        onChange={(e) => setFocusLines(parseInt(e.target.value))}
                        className="bg-[#1a1a2e] border border-white/20 rounded px-2 py-1 text-xs text-white"
                        style={{ colorScheme: 'dark' }}
                      >
                        <option value={2} className="bg-[#1a1a2e] text-white">2 lines</option>
                        <option value={3} className="bg-[#1a1a2e] text-white">3 lines</option>
                        <option value={4} className="bg-[#1a1a2e] text-white">4 lines</option>
                        <option value={5} className="bg-[#1a1a2e] text-white">5 lines</option>
                      </select>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Countdown Overlay */}
          {countdown > 0 && (
            <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-10">
              <motion.div
                key={countdown}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.5, opacity: 0 }}
                className="text-8xl font-bold text-teal-400"
              >
                {countdown}
              </motion.div>
            </div>
          )}

          {/* Content Area */}
          {!isMinimized && (
            <div
              ref={contentRef}
              className={`flex-1 overflow-hidden relative cursor-default ${
                windowMode === 'full' ? 'flex items-center justify-center' : ''
              }`}
              onMouseDown={(e) => e.stopPropagation()} // Prevent drag when scrolling content
            >
              {/* Focus mode darkness overlays */}
              {focusMode ? (
                <>
                  {/* Top darkness */}
                  <div
                    className="absolute inset-x-0 top-0 bg-black/80 z-10 pointer-events-none transition-all duration-300"
                    style={{
                      height: `calc(50% - ${(focusLines / 2) * (FONT_SIZE_MAP[fontSize] * LINE_HEIGHT_MAP[lineHeight])}px)`
                    }}
                  />
                  {/* Bottom darkness */}
                  <div
                    className="absolute inset-x-0 bottom-0 bg-black/80 z-10 pointer-events-none transition-all duration-300"
                    style={{
                      height: `calc(50% - ${(focusLines / 2) * (FONT_SIZE_MAP[fontSize] * LINE_HEIGHT_MAP[lineHeight])}px)`
                    }}
                  />
                </>
              ) : (
                <>
                  {/* Standard gradient overlays for readability */}
                  <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-[#001233] to-transparent z-10 pointer-events-none" />
                  <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#001233] to-transparent z-10 pointer-events-none" />
                </>
              )}

              {/* Center highlight line */}
              {highlightLine && (
                <div
                  className="absolute left-0 right-0 top-1/2 -translate-y-1/2 bg-teal-500/15 border-y border-teal-500/40 z-5 pointer-events-none"
                  style={{ height: FONT_SIZE_MAP[fontSize] * LINE_HEIGHT_MAP[lineHeight] + 8 }}
                />
              )}

              {/* Scrolling content */}
              <div
                className={`p-6 ${windowMode === 'full' ? 'max-w-4xl mx-auto w-full' : ''}`}
                style={{ paddingTop: '50%' }}
              >
                <div
                  ref={scrollRef}
                  className="will-change-transform"
                  style={{
                    transform: `translateY(-${scrollPosition}px) ${mirroring ? 'scaleX(-1)' : ''}`,
                    fontSize: FONT_SIZE_MAP[fontSize],
                    lineHeight: LINE_HEIGHT_MAP[lineHeight]
                  }}
                >
                  {aiAutoDetect && scriptWordsRef.current.length > 0 ? (
                    // Word-by-word rendering with highlighting for AI mode
                    <div style={{ minHeight: FONT_SIZE_MAP[fontSize] * LINE_HEIGHT_MAP[lineHeight] }}>
                      {scriptWordsRef.current.map((word, idx) => {
                        if (word.isSpace) {
                          return word.raw.includes('\n')
                            ? <br key={`sp-${idx}`} />
                            : <span key={`sp-${idx}`}> </span>;
                        }
                        const isMatched = word.index <= matchedWordIndex;
                        const isCurrentWord = word.index === matchedWordIndex;
                        return (
                          <span
                            key={`w-${idx}`}
                            data-word-idx={word.index}
                            style={{
                              transition: 'all 0.3s ease',
                              color: isMatched ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.55)',
                              textDecoration: isCurrentWord ? 'underline' : 'none',
                              textDecorationColor: isCurrentWord ? 'rgba(45, 212, 191, 0.6)' : 'transparent',
                              textUnderlineOffset: '4px',
                              textDecorationThickness: '2px',
                            }}
                          >{word.raw}</span>
                        );
                      })}
                    </div>
                  ) : (
                    lines.map((line, index) => {
                      const isCurrentLine = index === currentLine;
                      const focusHalfRange = Math.floor(focusLines / 2);
                      const isNearEnd = currentLine >= lines.length - focusHalfRange - 1;
                      const isNearCurrentLine = focusMode
                        ? (Math.abs(index - currentLine) <= focusHalfRange) ||
                          (isNearEnd && index >= lines.length - focusLines)
                        : true;

                      return (
                        <div
                          key={index}
                          className={`transition-all duration-300 ${
                            highlightLine && isCurrentLine
                              ? 'text-white font-medium scale-[1.02]'
                              : isNearCurrentLine
                                ? 'text-white/90'
                                : 'text-white/50'
                          }`}
                          style={{ minHeight: FONT_SIZE_MAP[fontSize] * LINE_HEIGHT_MAP[lineHeight] }}
                        >
                          {line || '\u00A0'}
                        </div>
                      );
                    })
                  )}
                  {/* Extra space at bottom */}
                  <div style={{ height: '50vh' }} />
                </div>
              </div>
            </div>
          )}

          {/* Controls */}
          <div
            className="p-3 border-t border-white/10 control-area"
            style={{ backgroundColor: `rgba(0, 18, 51, ${Math.min(opacity + 0.2, 1)})` }}
            onMouseDown={(e) => e.stopPropagation()} // Prevent drag when using controls
          >
            <div className="flex items-center justify-between gap-2">
              {/* Play/Pause */}
              <div className="flex items-center gap-2">
                {isScrolling ? (
                  <button
                    onClick={handlePause}
                    className="flex items-center justify-center w-8 h-8 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
                  >
                    <Pause className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleStart}
                    className="flex items-center justify-center w-8 h-8 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors"
                  >
                    <Play className="w-4 h-4" />
                  </button>
                )}

                <button
                  onClick={handleReset}
                  className="flex items-center justify-center w-8 h-8 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>

              {/* AI Auto-Detect Toggle */}
              <button
                onClick={() => setAiAutoDetect(!aiAutoDetect)}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                  aiAutoDetect
                    ? 'bg-gradient-to-r from-purple-600 to-teal-500 text-white'
                    : 'border border-white/20 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
                title="AI Auto-detect - follows your speech"
              >
                {aiProcessing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Zap className="w-3.5 h-3.5" />
                )}
                <span className="text-xs">AI</span>
                {aiListening && (
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                )}
              </button>

              {/* AI hearing indicator */}
              {aiAutoDetect && lastHeard && (
                <span className="text-xs text-green-400/70 truncate max-w-[120px]" title={lastHeard}>
                  🎤 {lastHeard}
                </span>
              )}
              {aiAutoDetect && !aiListening && !lastHeard && isScrolling && (
                <span className="text-xs text-amber-400/70 truncate max-w-[140px]">
                  {sttBotActive ? 'Waiting for speech...' : 'Starting STT...'}
                </span>
              )}

              {/* Speed controls - hidden when AI auto-detect is on */}
              {!aiAutoDetect && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleSpeedChange(-1)}
                    className="p-1.5 hover:bg-white/10 rounded"
                  >
                    <Minus className="w-4 h-4 text-gray-400" />
                  </button>
                  <span className="text-sm text-white w-8 text-center">{scrollSpeed}</span>
                  <button
                    onClick={() => handleSpeedChange(1)}
                    className="p-1.5 hover:bg-white/10 rounded"
                  >
                    <Plus className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              )}

              {/* Manual scroll */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setScrollPosition(prev => Math.max(0, prev - 50))}
                  className="p-1.5 hover:bg-white/10 rounded"
                >
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                </button>
                <button
                  onClick={() => setScrollPosition(prev => prev + 50)}
                  className="p-1.5 hover:bg-white/10 rounded"
                >
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Manual scroll slider */}
            <div className="mt-3">
              <Slider
                value={scrollPosition}
                onChange={(v) => {
                  setScrollPosition(v);
                  if (!aiAutoDetect) {
                    setIsScrolling(false);
                  } else {
                    // Update match time so AI doesn't fight manual positioning
                    lastMatchTimeRef.current = Date.now();
                  }
                }}
                min={0}
                max={scrollRef.current ? scrollRef.current.scrollHeight - (contentRef.current?.clientHeight || 300) : 1000}
                step={1}
              />
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  // Use portal to render outside the main React tree
  // This ensures teleprompter is completely isolated from stage/layout
  return ReactDOM.createPortal(
    teleprompterContent,
    document.body
  );
}
